import { useTranslation } from 'react-i18next'
import { SettingsSortBy, SettingsSortDir, useBusinessSettings } from '../../../contexts/BusinessSettingsContext'
import { useUser } from '../../../contexts/userContext'
import styles from '../Settings.module.css'
import { LicenceHolder } from './components/Players'
import sharedStyles from '../Settings.module.css'
import { LicenceGraphs } from './components/LicenceGraphs'
import { CellElement, Table, TableRow } from '../../reports/components/GenericTable'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { dateTimeToLocalDateString, softBreakToEmail } from '../../../util/string'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../../routes'
import { getIcon } from '../../../common/components/icons/utils'
import { Button } from '../../../common/components/button/Button'

export const LicenseHolderSettings: React.FC = () => {
  const { data } = useBusinessSettings()
  const { user } = useUser()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [activeSortCol, setActiveSortCol] = useState({ sortBy: SettingsSortBy.NAME, direction: SettingsSortDir.ASC })
  const [allLicenseHolders, setAllLicenseHolders] = useState<LicenceHolder[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)

  const { pinSeatHolders, emailSeatHolders } = data?.businessInfo.newBusinessInfo || {}

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
  }, [])

  const handleOnCancel = useCallback(() => {
    navigate(routes.organizationSettings)
  }, [navigate])

  const emailLicenseHolders = useMemo((): LicenceHolder[] => {
    const holders = (emailSeatHolders || '')
      .split(',')
      .filter((item) => item.length)
      .map((seatHolder): LicenceHolder => {
        return {
          name: seatHolder,
          type: t('settings_organization.players.email_type', 'eMail/Player account'),
          fixedType: 'email',
        }
      })
    return holders
  }, [emailSeatHolders, t])

  const pinLicenseHolders = useMemo((): LicenceHolder[] => {
    const holders = (pinSeatHolders || '')
      .split(',')
      .filter((item) => item.length)
      .map((seatHolder): LicenceHolder => {
        return {
          name: seatHolder,
          type: t('settings_organization.players.qr_pin_type', 'QR/PIN'),
          fixedType: 'QR',
        }
      })
    return holders
  }, [pinSeatHolders, t])

  const initialAllLicenseHolders = useMemo((): LicenceHolder[] => {
    return emailLicenseHolders.concat(pinLicenseHolders)
  }, [emailLicenseHolders, pinLicenseHolders])

  const sortLicenceHolders = useCallback(() => {
    if (!allLicenseHolders || allLicenseHolders.length < 2) return allLicenseHolders
    setAllLicenseHolders((prev) => {
      return prev.sort((a, b) => {
        if (activeSortCol.sortBy === SettingsSortBy.NAME) {
          return activeSortCol.direction === SettingsSortDir.ASC
            ? a.name.trim().toLocaleLowerCase() > b.name.trim().toLocaleLowerCase()
              ? 1
              : -1
            : a.name.trim().toLocaleLowerCase() < b.name.trim().toLocaleLowerCase()
            ? 1
            : -1
        }
        return activeSortCol.direction === SettingsSortDir.ASC ? (a.type > b.type ? 1 : -1) : a.type < b.type ? 1 : -1
      })
    })
  }, [activeSortCol.direction, activeSortCol.sortBy, allLicenseHolders])

  useEffect(() => {
    setAllLicenseHolders(initialAllLicenseHolders)
    sortLicenceHolders()
  }, [emailLicenseHolders, initialAllLicenseHolders, pinLicenseHolders, sortLicenceHolders])

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []
    for (let i = (currentPage - 1) * pageSize; i < currentPage * pageSize && i < allLicenseHolders.length; i++) {
      const row = allLicenseHolders[i] as LicenceHolder
      const tableRow: CellElement[] = [
        <div
          className={classNames(styles.cell, styles.nameCell)}
          dangerouslySetInnerHTML={{ __html: softBreakToEmail(row.name) }}
        />,
        <div className={classNames(styles.cell, styles.typeCell)}>
          {' '}
          <div
            className={classNames(
              styles.typeCellBox,
              row.fixedType === 'QR' ? styles.typeCellBoxLight : styles.typeCellBoxDark,
            )}
          />
          {row.type}
        </div>,
      ]
      rows.push(tableRow)
    }
    return rows
  }, [allLicenseHolders, currentPage, pageSize])

  const handleSort = useCallback(
    (sortKey: string) => (direction: SettingsSortDir | string) => {
      setActiveSortCol({ sortBy: sortKey as SettingsSortBy, direction: direction as SettingsSortDir })
      sortLicenceHolders()
    },
    [sortLicenceHolders],
  )

  const lastPage = Math.floor(allLicenseHolders.length / pageSize) + (allLicenseHolders.length % pageSize === 0 ? 0 : 1)
  const hasPlayers = allLicenseHolders.length > 0
  const allPageOptions = Array.from({ length: lastPage }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }))

  return (
    <>
      <div className={styles.section}>
        <div className={styles.licenceTitleContainer}>
          <span className={classNames(sharedStyles.icon)} onClick={handleOnCancel}>
            {getIcon('arrowLeft')}
          </span>
          <h1 className={sharedStyles.sectionTitle}>
            {t('settings_organization.licence_holders.title', 'All player licence users')}
          </h1>
        </div>
        <LicenceGraphs
          emailLicenseHolders={emailLicenseHolders.length}
          pinLicenseHolders={pinLicenseHolders.length}
          licenseValidUntilDate={dateTimeToLocalDateString(
            data?.businessInfo?.newBusinessInfo?.nextDateWhenLicencesFreed,
            user?.language,
          )}
          isRenewing={data?.businessInfo.newBusinessInfo?.autoRenew || false}
          licenceEndDate={dateTimeToLocalDateString(data?.businessInfo.validUntil, user?.language)}
        />
        <p className={styles.licencesUsed}>
          {t('settings_organization.licence_holders.licences_used', {
            player_count: allLicenseHolders.length,
            defaultValue: '%{player_count} licences used',
          })}
        </p>
        {hasPlayers && (
          <div ref={scrollRef}>
            <Table
              isSortable
              headerWidths={['70%', 'fit-content']}
              headerLabels={[
                t('settings_organization.licence_holders.table.header.name', 'Name'),
                t('settings_organization.licence_holders.table.header.type', 'Type'),
              ]}
              rows={tableRowData}
              hasPagination
              currentPage={currentPage}
              pageSize={pageSize}
              itemsPerPageLabel={t('settings_organization.licence_holders.table.per_page', 'player accounts per page')}
              allPageOptions={allPageOptions}
              lastPage={lastPage}
              //loading
              onChangeCurrentPage={(option) => {
                scrollRef.current?.scrollIntoView()
                setCurrentPage(option?.value || 1)
              }}
              onChangePageSize={(option) => {
                scrollRef.current?.scrollIntoView()
                setPageSize(option?.value || 10)
                setCurrentPage(1)
              }}
              onClickNext={() => {
                scrollRef.current?.scrollIntoView()
                setCurrentPage((currentPage || 0) + 1)
              }}
              onClickPrev={() => {
                scrollRef.current?.scrollIntoView()
                setCurrentPage((currentPage || 2) - 1)
              }}
              headerSortTags={[SettingsSortBy.NAME, SettingsSortBy.TYPE]}
              onSort={handleSort}
              activeSortCol={activeSortCol.sortBy}
            />
          </div>
        )}
        <div className={sharedStyles.bottomActions}>
          <Button onClick={handleOnCancel} variant='outline-normal'>
            {t('settings_organization.players.tags.buttons.return', 'Return')}
          </Button>
        </div>
      </div>
    </>
  )
}
